"use client";

import { CircleCheck, Info, OctagonX, TriangleAlert } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ComponentProps, type ReactNode, useEffect, useMemo } from "react";
import type { ActionState } from "@/components/forms/action-state.type";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast, Toast, ToastClose, ToastContent, ToastDescription, ToastPortal, ToastProvider, ToastTitle, ToastViewport, useToastManager } from "@/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getPublicEnv } from "@/config/get-public-env";

const LOOKS = {
  success: { icon: CircleCheck, tone: "border-emerald-500/50 bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100" },
  error: { icon: OctagonX, tone: "border-red-500/50 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100" },
  warning: { icon: TriangleAlert, tone: "border-amber-500/50 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100" },
  info: { icon: Info, tone: "border-sky-500/50 bg-sky-50 text-sky-900 dark:bg-sky-950 dark:text-sky-100" },
} as const;

function Messages() {
  const { toasts } = useToastManager();
  return toasts.map((item) => {
    const look = LOOKS[item.type as keyof typeof LOOKS] ?? LOOKS.info;
    const Icon = look.icon;
    return (
      <Toast key={item.id} toast={item} className={look.tone}>
        <ToastContent><Icon aria-hidden className="size-5 shrink-0" /><div className="flex min-w-0 flex-1 flex-col gap-1"><ToastTitle className="font-medium" /><ToastDescription /></div><ToastClose /></ToastContent>
      </Toast>
    );
  });
}

/** The place where pop-up messages appear (bottom corner of every page): at most `NEXT_PUBLIC_TOAST_LIMIT` at once, each gone after `NEXT_PUBLIC_TOAST_TIMEOUT_MS`, coloured by what it means (green good, red refused, amber careful, blue information). Mounted once, for all apps. */
export function AppToaster({ children }: { children: ReactNode }) {
  const env = getPublicEnv();
  return (
    <ToastProvider toastManager={toast} timeout={env.NEXT_PUBLIC_TOAST_TIMEOUT_MS} limit={env.NEXT_PUBLIC_TOAST_LIMIT}>
      {children}
      <ToastPortal><ToastViewport className="z-[100]"><Messages /></ToastViewport></ToastPortal>
    </ToastProvider>
  );
}

/** Shows one pop-up message when the page appears (for what happened before the page loaded, like coming back from the sign-in server) and renders nothing. */
export function ToastOnMount({ type, message }: { type: "success" | "error" | "warning" | "info"; message: string }) {
  useEffect(() => { toast.add({ type, title: message }); }, [type, message]);
  return null;
}

/** The way every screen tells the person what happened: a short pop-up message that fades by itself, green for good news (`success`), red for a refusal or failure (`error`), amber for something to watch (`warning`), blue for information (`info`). `result` turns what a form action returned into the right one: the reason in words when it was refused, "Saved" (or how many worked) when it worked, nothing when neither. */
export function useNotify() {
  const t = useTranslations("admin");
  return useMemo(() => {
    const show = (type: "success" | "error" | "warning" | "info") => (title: string) => { toast.add({ type, title }); };
    return {
      success: show("success"), error: show("error"), warning: show("warning"), info: show("info"),
      result: (state: ActionState) => {
        if (state.error) show("error")(t(`errors.${state.error}`));
        else if (state.done) show(state.failed ? "warning" : "success")(state.note ? t(`notes.${state.note}`) : state.count === undefined ? t("saved") : t("bulk.result", { count: state.count, failed: state.failed ?? 0 }));
      },
    };
  }, [t]);
}

/** The question window of the site (shadcn's alert dialog, never the browser's own box): a title, the `question`, a Cancel button and a Confirm button (red when `destructive`). `onConfirm` runs when it is confirmed and `onCancel` when it is cancelled, closed with Escape or clicked away; nothing is shown while `question` is null. */
export function ConfirmDialog({ question, destructive, onConfirm, onCancel }: { question: string | null; destructive?: boolean; onConfirm: () => void; onCancel: () => void }) {
  const t = useTranslations("confirm");
  return (
    <AlertDialog open={question !== null} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{t("title")}</AlertDialogTitle><AlertDialogDescription>{question}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction variant={destructive ? "destructive" : "default"} onClick={onConfirm}>{t("confirm")}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** An icon-only button: its `label` is both the accessible name and the tooltip. Defaults to a small outline button; any Button prop (and a menu or dialog trigger's props) passes through. */
export function TipButton({ label, ...props }: { label: string } & ComponentProps<typeof Button>) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Button type="button" variant="outline" size="icon-sm" aria-label={label} {...props} />} />
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
